import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { MeDto, Theme } from '@bingo/shared';
import { api, ApiError } from './api';
import { applyTheme, watchSystemTheme } from './theme';
import { useLang } from './i18n';
import type { Lang } from '@bingo/shared';

interface AuthState {
  me: MeDto | null;
  loading: boolean;
  /** True when the owner locked the app and this person is not the owner. */
  locked: boolean;
  refreshMe: () => Promise<void>;
  setMe: (me: MeDto | null) => void;
  logout: () => Promise<void>;
  setTheme: (theme: Theme) => Promise<void>;
  /** Changes the interface language now and keeps it on the account. */
  setLanguage: (lang: Lang) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const { lang, setLang } = useLang();

  const setMe = useCallback((value: MeDto | null) => {
    setMeState(value);
    if (value) {
      applyTheme(value.user.theme);
      setLang(value.user.language);
      setLocked(false);
    }
  }, [setLang]);

  useEffect(() => {
    const onLocked = () => {
      setLocked(true);
      setMeState(null);
    };
    window.addEventListener('bingo:app-locked', onLocked);
    return () => window.removeEventListener('bingo:app-locked', onLocked);
  }, []);

  const refreshMe = useCallback(async () => {
    setMe(await api<MeDto>('/api/auth/me'));
  }, [setMe]);

  useEffect(() => {
    api<MeDto>('/api/auth/me')
      .then(setMe)
      .catch((e) => {
        if (!(e instanceof ApiError && (e.status === 401 || e.status === 423))) console.error(e);
        setMe(null);
      })
      .finally(() => setLoading(false));
  }, [setMe]);

  useEffect(() => watchSystemTheme(() => me?.user.theme ?? 'system'), [me]);

  const setLanguage = useCallback(
    async (l: Lang) => {
      setLang(l); // instant, then saved on the account
      if (me) setMe(await api<MeDto>('/api/me/preferences', { method: 'PATCH', body: { language: l } }));
    },
    [me, setLang, setMe],
  );
  void lang;

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setMeState(null);
    setLocked(false);
  }, []);

  const setTheme = useCallback(
    async (theme: Theme) => {
      applyTheme(theme); // instant, then saved on the account
      setMe(await api<MeDto>('/api/me/preferences', { method: 'PATCH', body: { theme } }));
    },
    [setMe],
  );

  return <Ctx.Provider value={{ me, loading, locked, refreshMe, setMe, logout, setTheme, setLanguage }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
